import type { ErrorLogLevel, LogLevelError } from '$/types/LogLevelError';

/**
 * Problem details
 *
 * @see https://datatracker.ietf.org/doc/html/rfc7807
 */
export interface Problem {
  body?: string;
  type: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  cause?: Problem;
}

export class ThrowableProblem extends Error implements LogLevelError {
  readonly name = 'ThrowableProblem';
  readonly problem: Problem;
  readonly logLevel: ErrorLogLevel;

  constructor(
    body: Partial<Problem> & { [key: string]: unknown },
    options?: { logLevel?: ErrorLogLevel },
  ) {
    const problem = { type: 'about:blank', ...body };
    let message = body.type;
    if (body.title) message += `: ${body.title}`;
    if (body.detail) message += `: ${body.detail}`;
    const { type: _x, title: _y, detail: _z, ...rest } = body;
    super(`${message} ${JSON.stringify(rest)}`);
    this.problem = problem;
    this.logLevel = options?.logLevel ?? 'error';
  }
}

/**
 * Problem types `fetchWithProblem` produces when no response ever arrives.
 *
 * `FETCH_ERROR_TYPE` is exported because the retry rule in
 * `$/modules/graphql-client/policy` decides what to attempt again by comparing
 * against it: a string literal repeated in two files would let the two sides
 * drift apart silently.
 */
export const FETCH_ERROR_TYPE = '://fe-services/fetch/error';
export const FETCH_TIMEOUT_TYPE = '://fe-services/fetch/timeout';
export const FETCH_ABORTED_TYPE = '://fe-services/fetch/aborted';

/**
 * Tells apart the three ways a fetch can fail before a response arrives.
 *
 * Node rejects an interrupted request with a `DOMException` whose name carries
 * the distinction: `TimeoutError` when an `AbortSignal.timeout` deadline
 * expired, `AbortError` when something aborted the request explicitly. Anything
 * else — a refused or reset connection, DNS failure — surfaces as
 * `TypeError: fetch failed`. Verified on Node 24.13.0, the version pinned in
 * the Dockerfile.
 *
 * The distinction matters downstream: an expired deadline means the upstream
 * had its full budget and produced nothing, so retrying it only adds load,
 * while a connection that never opened is worth a second attempt.
 */
function problemFromFetchError(error: unknown, url: string): ThrowableProblem {
  if (error instanceof DOMException && error.name === 'TimeoutError') {
    return new ThrowableProblem({
      type: FETCH_TIMEOUT_TYPE,
      description: String(error),
      url,
    });
  }

  if (error instanceof DOMException && error.name === 'AbortError') {
    // Nothing is wrong upstream — whoever issued the request walked away. Same
    // reasoning as the ResponseAborted downgrade in `instrumentation.ts`.
    return new ThrowableProblem(
      {
        type: FETCH_ABORTED_TYPE,
        description: String(error),
        url,
      },
      { logLevel: 'warning' },
    );
  }

  return new ThrowableProblem({
    type: FETCH_ERROR_TYPE,
    description: String(error),
    url,
  });
}

/** Same as fetch, but handles problem responses */
export async function fetchWithProblem(
  url: string,
  requestInit?: RequestInit,
): Promise<Response> {
  let response: Response;
  try {
    console.debug('[fetch]', url);
    response = await fetch(url, requestInit);
  } catch (e) {
    throw problemFromFetchError(e, url);
  }
  const content = response.headers.get('content-type');
  if (content === 'application/problem+json') {
    const body = await response.json();
    throw new ThrowableProblem(body);
  }
  if (!response.ok) {
    throw new ThrowableProblem({
      type: `://fe-services/fetch/${response.status}`,
      status: response.status,
      body: await response.text(),
    });
  }
  return response;
}
